import { useEffect, useMemo, useState } from "react";
import { Col, Typography, Layout, Space, Button, message, Input } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import Header160 from "../../header/header";
import styles from "../ingresosPOStyles.module.css";
import BodegasTable from "./BodegasTable";
import BodegaForm from "./BodegaForm";
import BodegasPOCards from "./BodegasPOCards";
import { getBodegas, createBodega, updateBodega, deleteBodega } from "../../../services/financiero/ingresosPO/bodegasPOService";
import { getOrganizacionesPO, getResponsablesPO, getDepartamentosPO, getMunicipiosPO } from "../../../services/financiero/ingresosPO/catalogosPOService";
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../../store/auth/selectors';

const { Title, Text } = Typography;

export default function BodegasPage() {

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [organizaciones, setOrganizaciones] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [responsables, setResponsables] = useState([]);

  const appName = 'bodegas_ingresos_po';
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user.username;
  const permissionAccess = permissions.priv_access === 'Y';
  const permissionDelete = permissions.priv_delete === 'Y';
  const permissionUpdate = permissions.priv_update === 'Y';
  const permissionInsert = permissions.priv_insert === 'Y';

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getBodegas();
      setData(res);
    } catch (error) {
      message.error("Error cargando bodegas");
    }
    setLoading(false);
  };

  const cargarCatalogos = async () => {
    try {
      const [org, dep, mun, resp] = await Promise.all([
        getOrganizacionesPO(),
        getDepartamentosPO(),
        getMunicipiosPO(),
        getResponsablesPO()
      ]);
      setOrganizaciones(org);
      setDepartamentos(dep);
      setMunicipios(mun);
      setResponsables(resp);
    } catch (error) {
      message.error("Error cargando catálogos");
    }
  };

  useEffect(() => {
    cargar();
    cargarCatalogos();
  }, []);

  const dataFiltrada = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter((b) =>
      b.sigla?.toLowerCase().includes(q) ||
      b.organizacion_nombre?.toLowerCase().includes(q) ||
      b.municipio?.toLowerCase().includes(q) ||
      b.departamento?.toLowerCase().includes(q) ||
      b.responsable_nombre?.toLowerCase().includes(q)
    );
  }, [data, search]);

  const handleCreate = () => {
    if (!permissionInsert) {
      message.warning("No tiene permisos para crear bodegas");
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    if (!permissionUpdate) {
      message.warning("No tiene permisos para editar");
      return;
    }
    setEditing(record);
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    if (!permissionDelete) {
      message.warning("No tiene permisos para eliminar");
      return;
    }
    try {
      await deleteBodega(record.oid, userLogin);
      message.success("Bodega eliminada");
      cargar();
    } catch (error) {
      message.error(error?.response?.data?.message || "Error eliminando bodega");
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editing) {
        await updateBodega(editing.oid, values, userLogin);
        message.success("Bodega actualizada");
      } else {
        await createBodega(values, userLogin);
        message.success("Bodega creada");
      }
      setModalOpen(false);
      cargar();
    } catch (error) {
      message.error(error?.response?.data?.message || "Error guardando bodega");
    }
  };

  if (!permissionAccess) {
    return <div>No tiene permisos para acceder a este módulo</div>;
  }
  return (
    <div className={styles.ingresosPoModule}>
      <div className="container" style={{ paddingBottom: 20 }}>
        <Layout className="container">
          <div className="header-filter-container d-flex align-items-center justify-content-between" style={{ flexWrap: "wrap", gap: 16 }}>
            <Header160 />
            <Col flex="auto" style={{ textAlign: "center", minWidth: 260 }}>
              <Space direction="vertical" size={2} align="center">
                <Title level={4} style={{ margin: 0, color: "#042956", fontWeight: 600 }}>
                  Bodegas — Ingresos PO
                </Title>
                <Text style={{ color: "#6b7280", fontSize: 13 }}>
                  Catálogo de bodegas por organización
                </Text>
                {permissionInsert && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} style={{ marginTop: 8 }}>
                    Nueva bodega
                  </Button>
                )}
              </Space>
            </Col>
          </div>
        </Layout>
      </div>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <BodegasPOCards bodegas={data} />
        <Input
          allowClear
          placeholder="Buscar por sigla, organización, municipio o responsable"
          prefix={<SearchOutlined />}
          style={{ maxWidth: 420, marginBottom: 16 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <BodegasTable
          data={dataFiltrada}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          permissionDelete={permissionDelete}
          permissionUpdate={permissionUpdate}
        />
      </div>
      <BodegaForm
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        initialValues={editing}
        organizaciones={organizaciones}
        departamentos={departamentos}
        municipios={municipios}
        responsables={responsables}
      />
    </div>
  );
}
